import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { db } from '../firebase/config';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  doc, getDoc, setDoc, updateDoc, collection, getDocs, addDoc, runTransaction
} from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, TextField,
  Chip, IconButton, Switch, FormControlLabel,
  Divider, Alert, CircularProgress, ToggleButton,
  ToggleButtonGroup, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PROFILE_BLOCKS, EXPERIENCE_FIRST_ORDER, ACADEMIC_FIRST_ORDER
} from '../config/profileBlocks';
import CVImport from '../components/CVImport';
import ImageUpload from '../components/ImageUpload';
import RoleSelect from '../components/RoleSelect';
import AIProfileEvaluator from '../components/AIProfileEvaluator';
import SoftwareTagInput from '../components/SoftwareTagInput';
import SoftwareChecklist from '../components/SoftwareChecklist';
import { useSoftwareTags } from '../hooks/useSoftwareTags';
import { canEditProfile } from '../utils/permissions';
import {
  useProfileAutosave, TYPING_IDLE_MS, CHANGE_DEBOUNCE_MS
} from '../hooks/useProfileAutosave';
import { SaveMark, SparStatusRad } from '../components/SparStatus';
import {
  emptyProfile, pickProfile, missingRowIds, rowIdPatch, withRowIds,
  clearedProfile, newRowId, fieldWarning
} from '../utils/profileSync';

// ── Drag-and-drop för uppdrag ─────────────────────────────────────
function SortableAssignment({ a, onUpdate, onRemove, onFlush, saveStatus, currentUserId, departmentId, consultantTools, softwareRegistry, onAddSoftware }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: a.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    // Sparas när fokus lämnar ett fält i uppdraget (blur bubblar i React)
    <Paper ref={setNodeRef} style={style} variant="outlined" onBlur={onFlush}
      data-testid={'assignment-' + a.id}
      sx={{ p: 2, mb: 2, borderColor: saveStatus?.state === 'error' ? 'error.main' : undefined }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Drag-handtag */}
          <Box {...attributes} {...listeners}
            sx={{ cursor: 'grab', color: 'text.disabled', display: 'flex',
              alignItems: 'center', px: 0.5, '&:active': { cursor: 'grabbing' } }}>
            <DragIndicatorIcon fontSize="small" />
          </Box>
          <SaveMark status={saveStatus} />
        </Box>
        <IconButton size="small" color="error" onClick={() => onRemove(a.id)}>
          <DeleteIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Uppdragsnamn" value={a.name || ''} fullWidth
          onChange={e => onUpdate(a.id, 'name', e.target.value)} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Period" value={a.period || ''} fullWidth
            onChange={e => onUpdate(a.id, 'period', e.target.value)} />
          <TextField label="Projektbudget" value={a.budget || ''} fullWidth
            onChange={e => onUpdate(a.id, 'budget', e.target.value)} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <RoleSelect value={a.role || ''}
              onChange={val => onUpdate(a.id, 'role', val)}
              departmentId={departmentId} />
          </Box>
          <TextField label="Uppdragsgivare" value={a.client || ''} sx={{ flex: 1 }}
            onChange={e => onUpdate(a.id, 'client', e.target.value)} />
        </Box>
        <TextField label="Projektbeskrivning (gemensam)"
          value={a.projectDescription || ''} fullWidth multiline rows={3}
          helperText="Beskriver projektet – samma för alla konsulter"
          onChange={e => onUpdate(a.id, 'projectDescription', e.target.value)} />
        <TextField label="Min roll och insats"
          value={a.personalDescription || ''} fullWidth multiline rows={4}
          helperText="Din personliga insats i projektet"
          onChange={e => onUpdate(a.id, 'personalDescription', e.target.value)} />
        <Box>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ letterSpacing: 1 }}>
            PROGRAMVAROR SOM ANVÄNDES
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <SoftwareChecklist
              value={a.software || []}
              onChange={arr => onUpdate(a.id, 'software', arr, CHANGE_DEBOUNCE_MS)}
              consultantTools={consultantTools}
              registry={softwareRegistry}
              onAddNew={onAddSoftware}
            />
          </Box>
        </Box>
        <ImageUpload currentUrl={a.image}
          onUpload={url => onUpdate(a.id, 'image', url, 0)}
          path={'assignments/' + currentUserId}
          label="Ladda upp projektbild" />
      </Box>
    </Paper>
  );
}

function AssignmentsList({ assignments, onReorder, saveStatusFor, ...rowProps }) {
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  }));
  const ids = assignments.map(a => a.id);

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    onReorder(arrayMove(assignments, oldIndex, newIndex));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {assignments.map(a => (
          <SortableAssignment key={a.id} a={a}
            saveStatus={saveStatusFor(a.id)} {...rowProps} />
        ))}
      </SortableContext>
    </DndContext>
  );
}

// Radhuvud i listorna: kvittens för raden och ta bort-knapp
function RowHeader({ status, onRemove }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <SaveMark status={status} />
      <IconButton size="small" color="error" onClick={onRemove}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
}

const toDate = t => (t ? (t.toDate ? t.toDate() : new Date(t)) : null);

export default function Profile() {
  const { currentUser, userProfile } = useAuth();
  const { mode } = useTheme();
  const navigate = useNavigate();
  const { uid: routeUid } = useParams();
  // Vem redigeras – egen profil om ingen uid i URL:en
  const targetUid = routeUid || currentUser.uid;
  const isEditingOther = !!routeUid && routeUid !== currentUser.uid;
  // Sant först när vi vet att inloggad saknar behörighet (userProfile laddad)
  const accessDenied = isEditingOther && !!userProfile
    && !canEditProfile(userProfile, currentUser.uid, targetUid);
  // Behörigheten är känd: egen profil, eller annans när userProfile laddats
  const permissionKnown = !isEditingOther || !!userProfile;
  const canWrite = canEditProfile(userProfile, currentUser.uid, targetUid);
  const [targetName, setTargetName] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeSection, setActiveSection] = useState('personal');
  const [departmentId, setDepartmentId] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [restoreVersion, setRestoreVersion] = useState(null);
  const [leaveAction, setLeaveAction] = useState(null);
  const [infoDismissed, setInfoDismissed] = useState(false);
  // Sätts om databasen nekar skrivning – då sparas ingenting
  const [readOnly, setReadOnly] = useState(false);

  const { pool: softwarePool, registry: softwareRegistry, addToPool: addSoftware } = useSoftwareTags();

  // Självskattning
  const [skillRatings, setSkillRatings] = useState({});
  const [deptSkills, setDeptSkills] = useState([]);
  const [selfRatingEnabled, setSelfRatingEnabled] = useState(false);
  // Innehåll + profiltyp, blockordning och blockval – sparas automatiskt
  const [profile, setProfile] = useState(emptyProfile);

  const autosave = useProfileAutosave({
    uid: targetUid, editorUid: currentUser.uid, current: profile,
  });

  useEffect(() => {
    if (accessDenied) { setLoading(false); return; }
    // Vänta tills vi vet om användaren får skriva
    if (!permissionKnown) return;
    async function loadProfile() {
      const ref = doc(db, 'profiles', targetUid);
      try {
        const snap = await getDoc(ref);
        let data = snap.exists() ? snap.data() : null;
        let writable = canWrite;
        if (data && writable && missingRowIds(data)) {
          // Engångsskrivning: rader utan id får ett stabilt id, inget annat ändras
          try {
            data = await runTransaction(db, async tx => {
              const fresh = (await tx.get(ref)).data();
              const patch = rowIdPatch(fresh);
              if (Object.keys(patch).length) tx.update(ref, patch);
              return { ...fresh, ...patch };
            });
          } catch (err) {
            if (err.code !== 'permission-denied') throw err;
            writable = false;
          }
        }
        // Utan skrivbehörighet får raderna id bara lokalt, och ingenting sparas
        const loaded = pickProfile(writable ? data : withRowIds(data || {}));
        setProfile(loaded);
        if (writable) {
          autosave.begin(loaded, { exists: !!data, updatedAt: toDate(data?.updatedAt) });
        } else {
          setReadOnly(true);
        }
      } catch (err) {
        console.error('Profilen kunde inte läsas in', err);
        setLoadError('Profilen kunde inte läsas in. Ladda om sidan.');
        setLoading(false);
        return;
      }

      const userSnap = await getDoc(doc(db, 'users', targetUid));
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setTargetName(userData.displayName || '');
        const deptId = userData.departmentId || '';
        setDepartmentId(deptId);
        if (deptId) {
          const deptSnap = await getDoc(
            doc(db, 'departments', deptId));
          if (deptSnap.exists()) {
            setSelfRatingEnabled(
              !!deptSnap.data().selfRatingEnabled);
          }

          const skillSnap = await getDocs(
            collection(db, 'departments', deptId, 'skills')
          );
          const visible = skillSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(s => s.visibleToEmployee)
            .sort((a, b) => a.name.localeCompare(b.name));
          setDeptSkills(visible);

          const ratingsSnap = await getDocs(
            collection(db, 'profiles', targetUid, 'skillRatings')
          );
          const ratingsMap = {};
          ratingsSnap.docs.forEach(d => {
            ratingsMap[d.id] = d.data();
          });
          setSkillRatings(ratingsMap);
        }
      }

      setLoading(false);
    }
    loadProfile();
  // autosave.begin läser och skriver bara refs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUid, accessDenied, permissionKnown, canWrite]);

  function saveSkillRating(skillId, rating) {
    setSkillRatings(prev => ({
      ...prev,
      [skillId]: { ...prev[skillId], selfRating: rating }
    }));
    autosave.saveSide('skill.' + skillId, () => setDoc(
      doc(db, 'profiles', targetUid, 'skillRatings', skillId),
      { selfRating: rating, updatedAt: new Date() }, { merge: true }
    ));
  }

  async function loadVersions() {
    const versionsRef = collection(
      db, 'profiles', targetUid, 'versions');
    const snap = await getDocs(versionsRef);
    const vers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    vers.sort((a, b) =>
      toDate(b.createdAt) - toDate(a.createdAt));
    setVersions(vers);
    setShowVersions(true);
  }

  function versionLabel(prefix) {
    return prefix + ' ' +
      new Date().toLocaleDateString('sv-SE') + ' ' +
      new Date().toLocaleTimeString('sv-SE', {
        hour: '2-digit', minute: '2-digit'
      });
  }

  // ── Ändringar ─────────────────────────────────────────────────────
  // Text sparas vid blur och efter en paus i skrivandet, övrigt efter en kort debounce
  function change(fn, delay) {
    setProfile(prev => ({ ...prev, ...fn(prev) }));
    autosave.schedule(delay);
  }

  function handleProfileType(e, newType) {
    if (!newType) return;
    change(() => ({
      profileType: newType,
      blockOrder: newType === 'experience'
        ? EXPERIENCE_FIRST_ORDER : ACADEMIC_FIRST_ORDER,
    }), CHANGE_DEBOUNCE_MS);
  }

  function toggleBlock(id, on) {
    change(prev => ({
      visibleBlocks: on
        ? [...prev.visibleBlocks, id]
        : prev.visibleBlocks.filter(b => b !== id)
    }), CHANGE_DEBOUNCE_MS);
  }

  function updateField(section, value, delay = TYPING_IDLE_MS) {
    change(() => ({ [section]: value }), delay);
  }

  function updatePersonal(field, value, delay = TYPING_IDLE_MS) {
    change(prev => ({ personal: { ...prev.personal, [field]: value } }), delay);
  }

  function addItem(section, template) {
    change(prev => ({
      [section]: [{ id: newRowId(), ...template }, ...prev[section]]
    }), CHANGE_DEBOUNCE_MS);
  }

  function updateItem(section, id, field, value, delay = TYPING_IDLE_MS) {
    change(prev => ({
      [section]: prev[section].map(r => (r.id === id ? { ...r, [field]: value } : r))
    }), delay);
  }

  function removeItem(section, id) {
    change(prev => ({
      [section]: prev[section].filter(r => r.id !== id)
    }), CHANGE_DEBOUNCE_MS);
  }

  function addChip(section, value) {
    if (!value.trim()) return;
    change(prev => ({
      [section]: [...prev[section], value.trim()]
    }), CHANGE_DEBOUNCE_MS);
  }

  function removeChip(section, index) {
    change(prev => ({
      [section]: prev[section].filter((_, i) => i !== index)
    }), CHANGE_DEBOUNCE_MS);
  }

  function setTools(level, arr) {
    change(prev => ({ tools: { ...prev.tools, [level]: arr } }), CHANGE_DEBOUNCE_MS);
  }

  // AI-importen av CV sparas som en enda ändring när den är klar
  function handleImport(data) {
    setProfile(prev => {
      const merged = withRowIds({ ...prev, ...data });
      merged.assignments = (merged.assignments || []).map(a => ({ software: [], ...a }));
      return merged;
    });
    autosave.schedule(0);
  }

  function handleAIAccept(field, value) {
    if (field.startsWith('tools_')) {
      const level = field.slice('tools_'.length);
      change(prev => ({
        tools: { ...prev.tools, [level]: [...prev.tools[level], value] }
      }), CHANGE_DEBOUNCE_MS);
    } else if (field === 'languages') {
      change(prev => ({
        languages: [{ id: newRowId(), language: value, level: '' }, ...prev.languages]
      }), CHANGE_DEBOUNCE_MS);
    } else {
      change(prev => ({
        [field]: [...(prev[field] || []), value]
      }), CHANGE_DEBOUNCE_MS);
    }
  }

  // Props för ett textfält: sparas vid blur, kvittens intill fältet,
  // gul varning för ogiltigt värde (sparas ändå)
  function auto(key, { warn, helperText, sx } = {}) {
    const status = autosave.statusFor(key);
    const showWarn = warn && status?.state !== 'error';
    return {
      onBlur: () => autosave.flush(),
      error: status?.state === 'error',
      helperText: warn || helperText,
      sx: {
        ...(showWarn ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main' } } : {}),
        ...sx,
      },
      slotProps: {
        ...(showWarn ? { formHelperText: { sx: { color: 'warning.main' } } } : {}),
        ...(status ? { input: { endAdornment: (
          <InputAdornment position="end"><SaveMark status={status} /></InputAdornment>
        ) } } : {}),
      },
    };
  }

  // Profilens egna utgångar: spara först, varna om något ändå är osparat
  async function leave(go) {
    await autosave.flush();
    if (autosave.isDirty()) setLeaveAction(() => go);
    else go();
  }

  // Engångsinformation om att Spara-knappen är borta; stängningen sparas på användaren
  const showAutosaveInfo = !!userProfile
    && !userProfile.autosaveInfoDismissedAt && !infoDismissed;

  async function dismissAutosaveInfo() {
    setInfoDismissed(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid),
        { autosaveInfoDismissedAt: new Date() });
    } catch (err) {
      console.error('Kunde inte spara att informationsrutan stängts', err);
    }
  }

  const visibleSections = profile.blockOrder.filter(
    id => profile.visibleBlocks.includes(id));
  const isDark = mode === 'dark';

  const RATING_LABELS = {
    1: 'Grundläggande',
    3: 'God kunskap',
    5: 'Avancerad'
  };

  const CAT_INFO = {
    grundlaggande: {
      label: 'Grundläggande kompetenser',
      color: '#2e7d32', bg: '#e8f5e9'
    },
    nyckel: {
      label: 'Nyckelkompetenser',
      color: '#1565c0', bg: '#e3f2fd'
    },
    special: {
      label: 'Specialkompetenser',
      color: '#6a1b9a', bg: '#f3e5f5'
    },
  };

  if (accessDenied) return (
    <Container maxWidth="sm" sx={{ mt: 10 }}>
      <Alert severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => navigate(-1)}>
            Tillbaka
          </Button>
        }>
        Du har inte behörighet att redigera andra medarbetares konsultprofiler.
      </Alert>
    </Container>
  );

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (loadError) return (
    <Container maxWidth="sm" sx={{ mt: 10 }}>
      <Alert severity="error">{loadError}</Alert>
    </Container>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white',
        py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Logotypens egen navigering fångas, så att osparat hinner sparas */}
            <Box onClickCapture={e => {
              e.stopPropagation();
              leave(() => navigate('/'));
            }}>
              <LagbasLogo />
            </Box>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
            <IconButton color="inherit" aria-label="Tillbaka"
              onClick={() => leave(() => navigate(-1))}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              {isEditingOther
                ? `Konsultprofil – ${profile.personal?.name || targetName || 'medarbetare'}`
                : 'Min konsultprofil'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined"
              onClick={() => leave(() => navigate(isEditingOther ? `/cv-preview/${targetUid}` : '/cv-preview'))}
              sx={{ bgcolor: 'white', color: '#1a1a1a' }}>
              Förhandsgranska
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Sparstatus – fast högst upp vid scroll */}
      <SparStatusRad autosave={autosave} readOnly={readOnly} />

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {showAutosaveInfo && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={dismissAutosaveInfo}>
            Profilen sparas nu automatiskt när du lämnar ett fält.
            Det finns ingen Spara-knapp längre.
          </Alert>
        )}
        {isEditingOther && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Du redigerar <strong>{profile.personal?.name || targetName || 'en medarbetares'}</strong> konsultprofil.
            Ändringar sparas på medarbetarens profil.
          </Alert>
        )}
        <CVImport onImport={handleImport} />
        <AIProfileEvaluator
          profile={profile} onAccept={handleAIAccept} />

        <Box sx={{ display: 'flex', gap: 3,
          alignItems: 'flex-start' }}>

          {/* SIDOMENY */}
          <Paper sx={{ p: 2, minWidth: 210,
            position: 'sticky', top: 56 }}>
            <Typography variant="caption" color="text.secondary"
              fontWeight="bold"
              sx={{ px: 1, display: 'block', mb: 1,
                letterSpacing: 1 }}>
              PROFILBLOCK
            </Typography>

            {visibleSections.map(id => {
              const block = PROFILE_BLOCKS.find(b => b.id === id);
              if (!block) return null;
              const count =
                id === 'assignments'
                  ? profile.assignments?.length :
                id === 'employment'
                  ? profile.employment?.length :
                id === 'education'
                  ? profile.education?.length :
                id === 'certifications'
                  ? profile.certifications?.length :
                id === 'languages'
                  ? profile.languages?.length :
                id === 'publications'
                  ? profile.publications?.length :
                id === 'references'
                  ? profile.references?.length :
                id === 'board'
                  ? profile.board?.length :
                id === 'competencies'
                  ? profile.competencies?.length :
                id === 'tools'
                  ? ((profile.tools?.expert?.length || 0) +
                    (profile.tools?.good?.length || 0) +
                    (profile.tools?.basic?.length || 0)) :
                id === 'standards'
                  ? profile.standards?.length :
                id === 'personal_profile'
                  ? profile.personal_profile?.length :
                id === 'industries'
                  ? profile.industries?.length :
                null;

              return (
                <Box key={id}
                  onClick={() => setActiveSection(id)}
                  sx={{
                    display: 'flex', alignItems: 'center',
                    gap: 1, px: 1.5, py: 0.8, borderRadius: 1,
                    cursor: 'pointer', mb: 0.2,
                    bgcolor: activeSection === id
                      ? (isDark ? '#2a2a2a' : '#eeeeee')
                      : 'transparent',
                    borderLeft: activeSection === id
                      ? '3px solid #FF6B00'
                      : '3px solid transparent',
                    '&:hover': {
                      bgcolor: isDark ? '#222' : '#f0f0f0'
                    }
                  }}>
                  <Typography fontSize={13} sx={{ lineHeight: 1 }}>
                    {block.icon}
                  </Typography>
                  <Typography variant="caption"
                    fontWeight={activeSection === id
                      ? 'bold' : 'normal'}
                    sx={{ flex: 1, fontSize: '0.78rem' }}>
                    {block.label}
                  </Typography>
                  {count > 0 && (
                    <Chip label={count} size="small"
                      sx={{ height: 18, fontSize: '0.65rem',
                        bgcolor: activeSection === id
                          ? '#FF6B00'
                          : (isDark ? '#333' : '#e0e0e0'),
                        color: activeSection === id
                          ? 'white' : 'text.primary'
                      }} />
                  )}
                </Box>
              );
            })}

            {/* Kompetenskattning – bara om aktiverad */}
            {selfRatingEnabled && deptSkills.length > 0 && (
              <Box onClick={() => setActiveSection('skillratings')}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.5, py: 0.8, borderRadius: 1,
                  cursor: 'pointer', mb: 0.2,
                  bgcolor: activeSection === 'skillratings'
                    ? (isDark ? '#2a2a2a' : '#eeeeee')
                    : 'transparent',
                  borderLeft: activeSection === 'skillratings'
                    ? '3px solid #FF6B00'
                    : '3px solid transparent',
                  '&:hover': {
                    bgcolor: isDark ? '#222' : '#f0f0f0'
                  }
                }}>
                <Typography fontSize={13}>🎯</Typography>
                <Typography variant="caption"
                  fontWeight={activeSection === 'skillratings'
                    ? 'bold' : 'normal'}
                  sx={{ flex: 1, fontSize: '0.78rem' }}>
                  Kompetenskattning
                </Typography>
                <Chip
                  label={
                    Object.values(skillRatings).filter(
                      r => r.selfRating).length +
                    '/' + deptSkills.length
                  }
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem',
                    bgcolor: activeSection === 'skillratings'
                      ? '#FF6B00'
                      : (isDark ? '#333' : '#e0e0e0'),
                    color: activeSection === 'skillratings'
                      ? 'white' : 'text.primary'
                  }} />
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="caption" color="text.secondary"
              fontWeight="bold"
              sx={{ px: 1, display: 'block', mb: 1,
                letterSpacing: 1 }}>
              INSTÄLLNINGAR
            </Typography>

            {[
              { id: 'settings', icon: '⚙️', label: 'Inställningar' },
              { id: 'versions', icon: '🕓', label: 'Versioner' },
            ].map(item => (
              <Box key={item.id}
                onClick={() => setActiveSection(item.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.5, py: 0.8, borderRadius: 1,
                  cursor: 'pointer', mb: 0.2,
                  bgcolor: activeSection === item.id
                    ? (isDark ? '#2a2a2a' : '#eeeeee')
                    : 'transparent',
                  borderLeft: activeSection === item.id
                    ? '3px solid #FF6B00'
                    : '3px solid transparent',
                  '&:hover': {
                    bgcolor: isDark ? '#222' : '#f0f0f0'
                  }
                }}>
                <Typography fontSize={13}>{item.icon}</Typography>
                <Typography variant="caption"
                  fontWeight={activeSection === item.id
                    ? 'bold' : 'normal'}
                  sx={{ fontSize: '0.78rem' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}

            <Box onClick={() => setActiveSection('danger')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 0.8, borderRadius: 1,
                cursor: 'pointer', mb: 0.2,
                bgcolor: activeSection === 'danger'
                  ? '#ffebee' : 'transparent',
                borderLeft: activeSection === 'danger'
                  ? '3px solid #d32f2f'
                  : '3px solid transparent',
                '&:hover': { bgcolor: '#ffebee' }
              }}>
              <Typography fontSize={13} sx={{ color: '#d32f2f' }}>
                ⚠️
              </Typography>
              <Typography variant="caption"
                fontWeight={activeSection === 'danger'
                  ? 'bold' : 'normal'}
                sx={{ color: '#d32f2f', fontSize: '0.78rem' }}>
                Danger Zone
              </Typography>
            </Box>
          </Paper>

          {/* INNEHÅLL */}
          <Box sx={{ flex: 1 }}>

            {activeSection === 'personal' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold"
                  gutterBottom>
                  📸 Personuppgifter
                </Typography>
                <Box sx={{ display: 'flex',
                  flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <ImageUpload
                      currentUrl={profile.personal.photo}
                      onUpload={url => updatePersonal('photo', url, 0)}
                      path={'profiles/' + targetUid}
                      label="Ladda upp profilbild" variant="avatar" />
                    <SaveMark status={autosave.statusFor('personal.photo')} />
                  </Box>
                  <TextField {...auto('personal.name')} label="Fullständigt namn"
                    value={profile.personal.name}
                    onChange={e => updatePersonal('name',
                      e.target.value)} fullWidth />
                  <TextField {...auto('personal.title')} label="Titel / Roll"
                    value={profile.personal.title}
                    onChange={e => updatePersonal('title',
                      e.target.value)} fullWidth />
                  <TextField {...auto('personal.shortName', {
                      warn: fieldWarning('shortName', profile.personal.shortName),
                      helperText: 'Används som resursförkortning i budgetar',
                      sx: { maxWidth: 260 },
                    })}
                    label="Förkortning (2–4 tecken, t.ex. JOSV)"
                    value={profile.personal.shortName || ''}
                    onChange={e => updatePersonal('shortName',
                      e.target.value.toUpperCase().slice(0, 4))}
                    inputProps={{ maxLength: 4, style: { letterSpacing: 2, fontWeight: 700 } }} />
                  <TextField {...auto('personal.location')} label="Basort"
                    value={profile.personal.location}
                    onChange={e => updatePersonal('location',
                      e.target.value)} fullWidth />
                  <TextField {...auto('personal.email', {
                      warn: fieldWarning('email', profile.personal.email),
                    })}
                    label="E-post"
                    value={profile.personal.email}
                    onChange={e => updatePersonal('email',
                      e.target.value.toLowerCase())} fullWidth />
                  <TextField {...auto('personal.phone')} label="Telefon"
                    value={profile.personal.phone}
                    onChange={e => updatePersonal('phone',
                      e.target.value)} fullWidth />
                  <TextField {...auto('personal.linkedin', {
                      warn: fieldWarning('linkedin', profile.personal.linkedin),
                    })}
                    label="LinkedIn-adress"
                    value={profile.personal.linkedin || ''}
                    onChange={e => updatePersonal('linkedin',
                      e.target.value)} fullWidth
                    placeholder="https://www.linkedin.com/in/..." />
                </Box>
              </Paper>
            )}

            {activeSection === 'ingress' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold"
                  gutterBottom>
                  📝 Ingress
                </Typography>
                <Typography variant="body2" color="text.secondary"
                  gutterBottom>
                  En säljande sammanfattning – 2-4 meningar.
                </Typography>
                <TextField {...auto('ingress')} multiline rows={6} fullWidth
                  value={profile.ingress}
                  onChange={e => updateField('ingress',
                    e.target.value)}
                  placeholder="Beskriv din profil..." />
              </Paper>
            )}

            {activeSection === 'competencies' && (
              <Paper sx={{ p: 3 }}>
                <BlockWithChips
                  title="🏆 Kompetensområden"
                  items={profile.competencies}
                  saveStatus={autosave.statusFor('competencies')}
                  onAdd={(val) => addChip('competencies', val)}
                  onRemove={(i) => removeChip('competencies', i)}
                  placeholder="Lägg till kompetensområde..." />
              </Paper>
            )}

            {activeSection === 'assignments' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    💼 Uppdragserfarenhet i urval
                  </Typography>
                  <Button startIcon={<AddIcon />} variant="outlined"
                    onClick={() => addItem('assignments', {
                      projectId: '', name: '', period: '',
                      role: '', client: '', budget: '',
                      projectDescription: '',
                      personalDescription: '', image: '',
                      software: []
                    })}>
                    Lägg till uppdrag
                  </Button>
                </Box>
                <AssignmentsList
                  assignments={profile.assignments}
                  onReorder={newArr => updateField('assignments', newArr, CHANGE_DEBOUNCE_MS)}
                  onUpdate={(id, field, value, delay) => updateItem('assignments', id, field, value, delay)}
                  onRemove={id => removeItem('assignments', id)}
                  onFlush={() => autosave.flush()}
                  saveStatusFor={id => autosave.statusFor('assignments.' + id)}
                  currentUserId={targetUid}
                  departmentId={departmentId}
                  consultantTools={[
                    ...(profile.tools?.expert || []),
                    ...(profile.tools?.good || []),
                    ...(profile.tools?.basic || []),
                  ]}
                  softwareRegistry={softwareRegistry}
                  onAddSoftware={addSoftware}
                />
              </Paper>
            )}

            {activeSection === 'employment' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    🏢 Anställningar
                  </Typography>
                  <Button startIcon={<AddIcon />} variant="outlined"
                    onClick={() => addItem('employment', {
                      company: '', title: '',
                      period: '', description: ''
                    })}>
                    Lägg till anställning
                  </Button>
                </Box>
                {profile.employment.map(e => (
                  <Paper key={e.id} variant="outlined"
                    sx={{ p: 2, mb: 2 }}>
                    <RowHeader status={autosave.statusFor('employment.' + e.id)}
                      onRemove={() => removeItem('employment', e.id)} />
                    <Box sx={{ display: 'flex',
                      flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField {...auto(`employment.${e.id}.company`)} label="Företag"
                          value={e.company || ''} fullWidth
                          onChange={ev => updateItem('employment',
                            e.id, 'company', ev.target.value)} />
                        <TextField {...auto(`employment.${e.id}.period`)} label="Period"
                          value={e.period || ''} fullWidth
                          onChange={ev => updateItem('employment',
                            e.id, 'period', ev.target.value)} />
                      </Box>
                      <TextField {...auto(`employment.${e.id}.title`)} label="Befattning"
                        value={e.title || ''} fullWidth
                        onChange={ev => updateItem('employment',
                          e.id, 'title', ev.target.value)} />
                      <TextField {...auto(`employment.${e.id}.description`)} label="Beskrivning"
                        value={e.description || ''}
                        fullWidth multiline rows={3}
                        onChange={ev => updateItem('employment',
                          e.id, 'description', ev.target.value)} />
                    </Box>
                  </Paper>
                ))}
              </Paper>
            )}

            {activeSection === 'education' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    🎓 Utbildning
                  </Typography>
                  <Button startIcon={<AddIcon />} variant="outlined"
                    onClick={() => addItem('education', {
                      degree: '', school: '',
                      period: '', description: ''
                    })}>
                    Lägg till utbildning
                  </Button>
                </Box>
                {profile.education.map(e => (
                  <Paper key={e.id} variant="outlined"
                    sx={{ p: 2, mb: 2 }}>
                    <RowHeader status={autosave.statusFor('education.' + e.id)}
                      onRemove={() => removeItem('education', e.id)} />
                    <Box sx={{ display: 'flex',
                      flexDirection: 'column', gap: 2 }}>
                      <TextField {...auto(`education.${e.id}.degree`)} label="Examen / Utbildning"
                        value={e.degree || ''} fullWidth
                        onChange={ev => updateItem('education',
                          e.id, 'degree', ev.target.value)} />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField {...auto(`education.${e.id}.school`)} label="Skola / Universitet"
                          value={e.school || ''} fullWidth
                          onChange={ev => updateItem('education',
                            e.id, 'school', ev.target.value)} />
                        <TextField {...auto(`education.${e.id}.period`)} label="Period"
                          value={e.period || ''} fullWidth
                          onChange={ev => updateItem('education',
                            e.id, 'period', ev.target.value)} />
                      </Box>
                      <TextField {...auto(`education.${e.id}.description`)} label="Beskrivning"
                        value={e.description || ''}
                        fullWidth multiline rows={2}
                        onChange={ev => updateItem('education',
                          e.id, 'description', ev.target.value)} />
                    </Box>
                  </Paper>
                ))}
              </Paper>
            )}

            {activeSection === 'certifications' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    📚 Certifieringar och Övrig utbildning
                  </Typography>
                  <Button startIcon={<AddIcon />} variant="outlined"
                    onClick={() => addItem('certifications', {
                      name: '', issuer: '', year: ''
                    })}>
                    Lägg till
                  </Button>
                </Box>
                {profile.certifications.map(c => (
                  <Paper key={c.id} variant="outlined"
                    sx={{ p: 2, mb: 2 }}>
                    <RowHeader status={autosave.statusFor('certifications.' + c.id)}
                      onRemove={() => removeItem('certifications', c.id)} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField {...auto(`certifications.${c.id}.name`)} label="Namn"
                        value={c.name || ''} fullWidth
                        onChange={e => updateItem('certifications',
                          c.id, 'name', e.target.value)} />
                      <TextField {...auto(`certifications.${c.id}.issuer`)} label="Utfärdare"
                        value={c.issuer || ''} fullWidth
                        onChange={e => updateItem('certifications',
                          c.id, 'issuer', e.target.value)} />
                      <TextField {...auto(`certifications.${c.id}.year`, { sx: { width: 120 } })}
                        label="År" value={c.year || ''}
                        onChange={e => updateItem('certifications',
                          c.id, 'year', e.target.value)} />
                    </Box>
                  </Paper>
                ))}
              </Paper>
            )}

            {activeSection === 'tools' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold"
                  gutterBottom>
                  💻 IT och Verktyg
                </Typography>
                <Box sx={{ display: 'flex',
                  flexDirection: 'column', gap: 3 }}>
                  {[
                    { key: 'expert',
                      label: '⭐⭐⭐ Mycket goda kunskaper' },
                    { key: 'good',
                      label: '⭐⭐ Goda kunskaper' },
                    { key: 'basic',
                      label: '⭐ Grundläggande kunskaper' }
                  ].map(level => (
                    <Box key={level.key}>
                      <Typography variant="subtitle2"
                        fontWeight="bold" gutterBottom>
                        {level.label}{' '}
                        <SaveMark status={autosave.statusFor('tools.' + level.key)} />
                      </Typography>
                      <SoftwareTagInput
                        value={profile.tools[level.key]}
                        onChange={arr => setTools(level.key, arr)}
                        pool={softwarePool}
                        onAddNew={addSoftware}
                        label="Lägg till verktyg"
                        placeholder="Sök eller lägg till programvara..."
                      />
                      <Divider sx={{ mt: 2 }} />
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {activeSection === 'languages' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    🌍 Språk
                  </Typography>
                  <Button startIcon={<AddIcon />} variant="outlined"
                    onClick={() => addItem('languages', {
                      language: '', level: ''
                    })}>
                    Lägg till språk
                  </Button>
                </Box>
                {profile.languages.map(l => (
                  <Paper key={l.id} variant="outlined"
                    sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2,
                      alignItems: 'center' }}>
                      <TextField {...auto(`languages.${l.id}.language`)} label="Språk"
                        value={l.language || ''} fullWidth
                        onChange={e => updateItem('languages',
                          l.id, 'language', e.target.value)} />
                      <TextField {...auto(`languages.${l.id}.level`)} label="Nivå"
                        value={l.level || ''} fullWidth
                        placeholder="t.ex. Modersmål, Flytande"
                        onChange={e => updateItem('languages',
                          l.id, 'level', e.target.value)} />
                      <SaveMark status={autosave.statusFor('languages.' + l.id)} />
                      <IconButton color="error"
                        onClick={() => removeItem('languages', l.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Paper>
            )}

            {activeSection === 'standards' && (
              <Paper sx={{ p: 3 }}>
                <BlockWithChips
                  title="📋 Standarder och Regelverk"
                  items={profile.standards}
                  saveStatus={autosave.statusFor('standards')}
                  onAdd={(val) => addChip('standards', val)}
                  onRemove={(i) => removeChip('standards', i)}
                  placeholder="t.ex. ISO 19650, IFC, BEAst..." />
              </Paper>
            )}

            {activeSection === 'personal_profile' && (
              <Paper sx={{ p: 3 }}>
                <BlockWithChips
                  title="🤝 Personlig profil"
                  items={profile.personal_profile}
                  saveStatus={autosave.statusFor('personal_profile')}
                  onAdd={(val) => addChip('personal_profile', val)}
                  onRemove={(i) =>
                    removeChip('personal_profile', i)}
                  placeholder="t.ex. Erfaren förändringsledare..." />
              </Paper>
            )}

            {activeSection === 'industries' && (
              <Paper sx={{ p: 3 }}>
                <BlockWithChips
                  title="🏭 Branscherfarenhet"
                  items={profile.industries}
                  saveStatus={autosave.statusFor('industries')}
                  onAdd={(val) => addChip('industries', val)}
                  onRemove={(i) => removeChip('industries', i)}
                  placeholder="t.ex. Statliga byggnadsminnen..." />
              </Paper>
            )}

            {activeSection === 'project_scale' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold"
                  gutterBottom>
                  📊 Projektomfattning
                </Typography>
                <TextField {...auto('project_scale')} multiline rows={4} fullWidth
                  value={profile.project_scale || ''}
                  onChange={e => updateField('project_scale',
                    e.target.value)}
                  placeholder="Beskriv din erfarenhet..." />
              </Paper>
            )}

            {activeSection === 'publications' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    🎤 Publikationer och Föredrag
                  </Typography>
                  <Button startIcon={<AddIcon />} variant="outlined"
                    onClick={() => addItem('publications', {
                      title: '', venue: '', year: ''
                    })}>
                    Lägg till
                  </Button>
                </Box>
                {profile.publications.map(p => (
                  <Paper key={p.id} variant="outlined"
                    sx={{ p: 2, mb: 2 }}>
                    <RowHeader status={autosave.statusFor('publications.' + p.id)}
                      onRemove={() => removeItem('publications', p.id)} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField {...auto(`publications.${p.id}.title`)} label="Titel"
                        value={p.title || ''} fullWidth
                        onChange={e => updateItem('publications',
                          p.id, 'title', e.target.value)} />
                      <TextField {...auto(`publications.${p.id}.venue`)} label="Konferens / Publikation"
                        value={p.venue || ''} fullWidth
                        onChange={e => updateItem('publications',
                          p.id, 'venue', e.target.value)} />
                      <TextField {...auto(`publications.${p.id}.year`, { sx: { width: 120 } })}
                        label="År" value={p.year || ''}
                        onChange={e => updateItem('publications',
                          p.id, 'year', e.target.value)} />
                    </Box>
                  </Paper>
                ))}
              </Paper>
            )}

            {activeSection === 'references' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    👥 Referenspersoner
                  </Typography>
                  <Button startIcon={<AddIcon />} variant="outlined"
                    onClick={() => addItem('references', {
                      name: '', role: '', company: '',
                      phone: '', email: ''
                    })}>
                    Lägg till referens
                  </Button>
                </Box>
                {profile.references.map(r => (
                  <Paper key={r.id} variant="outlined"
                    sx={{ p: 2, mb: 2 }}>
                    <RowHeader status={autosave.statusFor('references.' + r.id)}
                      onRemove={() => removeItem('references', r.id)} />
                    <Box sx={{ display: 'flex',
                      flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField {...auto(`references.${r.id}.name`)} label="Namn"
                          value={r.name || ''} fullWidth
                          onChange={e => updateItem('references',
                            r.id, 'name', e.target.value)} />
                        <TextField {...auto(`references.${r.id}.role`)} label="Roll"
                          value={r.role || ''} fullWidth
                          onChange={e => updateItem('references',
                            r.id, 'role', e.target.value)} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField {...auto(`references.${r.id}.company`)} label="Företag"
                          value={r.company || ''} fullWidth
                          onChange={e => updateItem('references',
                            r.id, 'company', e.target.value)} />
                        <TextField {...auto(`references.${r.id}.phone`)} label="Telefon"
                          value={r.phone || ''} fullWidth
                          onChange={e => updateItem('references',
                            r.id, 'phone', e.target.value)} />
                        <TextField {...auto(`references.${r.id}.email`, {
                            warn: fieldWarning('email', r.email),
                          })}
                          label="E-post"
                          value={r.email || ''} fullWidth
                          onChange={e => updateItem('references',
                            r.id, 'email', e.target.value)} />
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Paper>
            )}

            {activeSection === 'licenses' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold"
                  gutterBottom>
                  🚗 Licenser
                </Typography>
                <TextField {...auto('licenses')} multiline rows={3} fullWidth
                  value={profile.licenses || ''}
                  onChange={e => updateField('licenses',
                    e.target.value)}
                  placeholder="t.ex. B-körkort, Drönarkort A1/A3..." />
              </Paper>
            )}

            {activeSection === 'board' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    🤝 Förtroendeuppdrag
                  </Typography>
                  <Button startIcon={<AddIcon />} variant="outlined"
                    onClick={() => addItem('board', {
                      role: '', org: '', period: ''
                    })}>
                    Lägg till
                  </Button>
                </Box>
                {profile.board.map(b => (
                  <Paper key={b.id} variant="outlined"
                    sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2,
                      alignItems: 'center' }}>
                      <TextField {...auto(`board.${b.id}.role`)} label="Roll"
                        value={b.role || ''} fullWidth
                        onChange={e => updateItem('board',
                          b.id, 'role', e.target.value)} />
                      <TextField {...auto(`board.${b.id}.org`)} label="Organisation"
                        value={b.org || ''} fullWidth
                        onChange={e => updateItem('board',
                          b.id, 'org', e.target.value)} />
                      <TextField {...auto(`board.${b.id}.period`, { sx: { width: 180 } })}
                        label="Period"
                        value={b.period || ''}
                        onChange={e => updateItem('board',
                          b.id, 'period', e.target.value)} />
                      <SaveMark status={autosave.statusFor('board.' + b.id)} />
                      <IconButton color="error"
                        onClick={() => removeItem('board', b.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Paper>
            )}

            {/* KOMPETENSKATTNING */}
            {activeSection === 'skillratings' && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold"
                  gutterBottom>
                  🎯 Kompetenskattning
                </Typography>
                <Typography variant="body2" color="text.secondary"
                  gutterBottom>
                  Skatta din nivå på varje kompetens.
                  Din gruppledare ser skattningarna i GAP-analysen.
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }} icon={false}>
                  <Typography variant="caption">
                    <strong>Skala:</strong> 1 = Grundläggande
                    kunskaper &nbsp;·&nbsp;
                    3 = Goda kunskaper &nbsp;·&nbsp;
                    5 = Avancerad / Expert
                  </Typography>
                </Alert>

                {['grundlaggande', 'nyckel', 'special'].map(cat => {
                  const catSkills = deptSkills.filter(
                    s => s.category === cat);
                  if (!catSkills.length) return null;
                  const catInfo = CAT_INFO[cat];

                  return (
                    <Box key={cat} sx={{ mb: 3 }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center',
                        gap: 1, mb: 1.5, p: 1,
                        bgcolor: catInfo.bg, borderRadius: 1
                      }}>
                        <Box sx={{ width: 10, height: 10,
                          borderRadius: '50%',
                          bgcolor: catInfo.color }} />
                        <Typography variant="subtitle2"
                          fontWeight="bold"
                          sx={{ color: catInfo.color }}>
                          {catInfo.label}
                        </Typography>
                      </Box>

                      {catSkills.map(skill => {
                        const current =
                          skillRatings[skill.id]?.selfRating;
                        return (
                          <Box key={skill.id} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2, mb: 1.5, p: 1.5,
                            bgcolor: current
                              ? catInfo.bg : 'action.hover',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: current
                              ? catInfo.color + '40' : 'divider'
                          }}>
                            <Typography variant="body2"
                              sx={{ flex: 1 }}>
                              {skill.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {[1, 3, 5].map(val => (
                                <Box key={val}
                                  onClick={() =>
                                    saveSkillRating(skill.id, val)}
                                  sx={{
                                    width: 36, height: 36,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem',
                                    border: '2px solid',
                                    borderColor: current === val
                                      ? catInfo.color : 'divider',
                                    bgcolor: current === val
                                      ? catInfo.color
                                      : 'background.paper',
                                    color: current === val
                                      ? 'white' : 'text.secondary',
                                    transition: 'all 0.15s',
                                    '&:hover': {
                                      borderColor: catInfo.color,
                                      bgcolor: current === val
                                        ? catInfo.color : catInfo.bg,
                                      color: current === val
                                        ? 'white' : catInfo.color
                                    }
                                  }}>
                                  {val}
                                </Box>
                              ))}
                              {current && (
                                <Box
                                  onClick={() =>
                                    saveSkillRating(skill.id, null)}
                                  sx={{
                                    width: 36, height: 36,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid',
                                    borderColor: 'divider',
                                    color: 'text.disabled',
                                    fontSize: '0.8rem',
                                    '&:hover': {
                                      borderColor: '#c62828',
                                      color: '#c62828'
                                    }
                                  }}>
                                  ✕
                                </Box>
                              )}
                            </Box>
                            <Typography variant="caption"
                              sx={{ minWidth: 90,
                                color: current
                                  ? catInfo.color : 'text.disabled',
                                fontWeight: current ? 600 : 400
                              }}>
                              {RATING_LABELS[current] || 'Ej skattad'}
                            </Typography>
                            <SaveMark status={autosave.sideStatusFor('skill.' + skill.id)} />
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}

                {deptSkills.length === 0 && (
                  <Alert severity="info">
                    Din gruppledare har inte aktiverat några
                    kompetenser för självskattning ännu.
                  </Alert>
                )}
              </Paper>
            )}

            {activeSection === 'settings' && (
              <Box sx={{ display: 'flex', flexDirection: 'column',
                gap: 3 }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold"
                    gutterBottom>
                    ⚙️ Profiltyp
                  </Typography>
                  <ToggleButtonGroup value={profile.profileType} exclusive
                    onChange={handleProfileType} size="small">
                    <ToggleButton value="experience">
                      <WorkIcon sx={{ mr: 1 }} fontSize="small" />
                      Erfarenhet först
                    </ToggleButton>
                    <ToggleButton value="academic">
                      <SchoolIcon sx={{ mr: 1 }} fontSize="small" />
                      Akademiker först
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Box component="span" sx={{ ml: 1.5 }}>
                    <SaveMark status={autosave.statusFor('profileType')} />
                  </Box>
                  <Typography variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}>
                    {profile.profileType === 'experience'
                      ? 'Uppdrag visas före utbildning'
                      : 'Utbildning visas före uppdrag'}
                  </Typography>
                </Paper>

                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold"
                    gutterBottom>
                    Valbara block
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap',
                    gap: 1 }}>
                    {PROFILE_BLOCKS.filter(
                      b => !b.defaultVisible).map(block => (
                      <FormControlLabel key={block.id}
                        control={
                          <Switch size="small"
                            checked={profile.visibleBlocks.includes(block.id)}
                            onChange={e => toggleBlock(block.id, e.target.checked)}
                          />
                        }
                        label={<>
                          {block.icon + ' ' + block.label}{' '}
                          <SaveMark status={autosave.statusFor('visibleBlocks.' + block.id)} />
                        </>}
                      />
                    ))}
                  </Box>
                </Paper>
              </Box>
            )}

            {activeSection === 'versions' && (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    🕓 Versionshistorik
                  </Typography>
                  <Button size="small" variant="outlined"
                    onClick={loadVersions}>
                    Ladda versioner
                  </Button>
                </Box>
                {versions.length > 0 ? (
                  versions.map(v => (
                    <Box key={v.id} sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: isDark ? '#1e1e1e' : '#f9f9f9',
                      borderRadius: 1, mb: 1
                    }}>
                      <Box>
                        <Typography variant="body2"
                          fontWeight="bold">
                          {v.label}
                        </Typography>
                        <Typography variant="caption"
                          color="text.secondary">
                          {v.snapshot?.assignments?.length || 0}{' '}
                          uppdrag,{' '}
                          {v.snapshot?.employment?.length || 0}{' '}
                          anställningar
                        </Typography>
                      </Box>
                      <Button size="small" variant="outlined"
                        onClick={() => setRestoreVersion(v)}>
                        Återställ
                      </Button>
                    </Box>
                  ))
                ) : showVersions ? (
                  <Typography variant="body2"
                    color="text.secondary">
                    Inga sparade versioner.
                  </Typography>
                ) : (
                  <Typography variant="body2"
                    color="text.secondary">
                    Klicka på "Ladda versioner" för att se historiken.
                  </Typography>
                )}
              </Paper>
            )}

            {activeSection === 'danger' && (
              <Paper sx={{ p: 3, border: '2px solid #d32f2f',
                borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold"
                  color="error" gutterBottom>
                  ⚠️ Danger Zone
                </Typography>
                <Divider sx={{ mb: 2, borderColor: '#d32f2f' }} />
                <Box sx={{ display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Rensa hela profilen
                    </Typography>
                    <Typography variant="body2"
                      color="text.secondary">
                      Skapar en ny tom profil. Din nuvarande version
                      sparas i versionshistoriken.
                    </Typography>
                  </Box>
                  <Button variant="outlined" color="error"
                    onClick={() => setConfirmClear(true)}>
                    Rensa profil
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      </Container>

      {/* Dialog rensa profil */}
      <Dialog open={confirmClear}
        onClose={() => setConfirmClear(false)}
        maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f' }}>
          Rensa profil?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Din nuvarande profil sparas i versionshistoriken
            innan den rensas.
          </Typography>
          <Typography variant="body2" color="text.secondary"
            sx={{ mt: 1 }}>
            Skriv <strong>RENSA</strong> för att bekräfta:
          </Typography>
          <TextField fullWidth size="small" sx={{ mt: 1 }}
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="RENSA" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setConfirmClear(false); setConfirmText('');
          }}>
            Avbryt
          </Button>
          <Button variant="contained" color="error"
            disabled={confirmText !== 'RENSA'}
            onClick={async () => {
              try {
                await addDoc(collection(db, 'profiles', targetUid, 'versions'), {
                  snapshot: { ...profile }, createdAt: new Date(),
                  label: versionLabel('Version')
                });
              } catch (err) {
                console.error('Kunde inte spara version före rensning', err);
                return;
              }
              // Rensningen räknas som en ändring och sparas direkt
              setProfile(clearedProfile(profile));
              autosave.markVersioned();
              autosave.schedule(0);
              setConfirmClear(false);
              setConfirmText('');
              loadVersions();
            }}>
            Rensa permanent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog återställ version */}
      <Dialog open={!!restoreVersion}
        onClose={() => setRestoreVersion(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle>Återställ version?</DialogTitle>
        <DialogContent>
          <Typography>
            Detta skriver över din nuvarande profil med versionen
            från {restoreVersion?.label}.
          </Typography>
          <Typography variant="body2" color="text.secondary"
            sx={{ mt: 1 }}>
            Din nuvarande profil sparas automatiskt först.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreVersion(null)}>
            Avbryt
          </Button>
          <Button variant="contained" onClick={async () => {
            try {
              await addDoc(collection(db, 'profiles', targetUid, 'versions'), {
                snapshot: { ...profile }, createdAt: new Date(),
                label: versionLabel('Auto-sparad')
              });
            } catch (err) {
              console.error('Kunde inte spara version före återställning', err);
              return;
            }
            // Versioner utan profiltyp/blockval behåller nuvarande val
            setProfile(withRowIds(pickProfile({
              profileType: profile.profileType,
              blockOrder: profile.blockOrder,
              visibleBlocks: profile.visibleBlocks,
              ...(restoreVersion.snapshot || {}),
            })));
            autosave.markVersioned();
            autosave.schedule(0);
            setRestoreVersion(null);
            loadVersions();
          }}>
            Ja, återställ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Varning när osparat finns kvar vid profilens egna utgångar */}
      <Dialog open={!!leaveAction} onClose={() => setLeaveAction(null)}
        maxWidth="xs" fullWidth>
        <DialogTitle>Ändringar är inte sparade</DialogTitle>
        <DialogContent>
          <Typography>
            Alla ändringar kunde inte sparas. Lämnar du profilen nu
            kan de gå förlorade.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveAction(null)}>
            Stanna kvar
          </Button>
          <Button color="error" onClick={() => {
            const go = leaveAction;
            setLeaveAction(null);
            go();
          }}>
            Lämna ändå
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function BlockWithChips({ title, items, onAdd, onRemove, placeholder, saveStatus }) {
  const [input, setInput] = useState('');
  return (
    <Box>
      {title && (
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}{' '}
          <SaveMark status={saveStatus} />
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField size="small" fullWidth value={input}
          placeholder={placeholder}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { onAdd(input); setInput(''); }
          }}
        />
        <Button variant="outlined" aria-label="Lägg till"
          onClick={() => { onAdd(input); setInput(''); }}>
          <AddIcon />
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {items.map((item, i) => (
          <Chip key={i} label={item}
            onDelete={() => onRemove(i)} />
        ))}
      </Box>
    </Box>
  );
}
